import { numberFormat } from '@libs/core';
import { ColorShades, Theme } from '@libs/types';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import parse, { attributesToProps, domToReact, HTMLReactParserOptions } from 'html-react-parser';
import React, { useMemo } from 'react';
import { TCoreTextData, TTextTransformation } from '../types/data';
import styles from './text.module.css';

type ThemeColors = Theme['colors'];

const DATA_ACTION_NAME = 'data-action-name';

const getColorFromPalette = (textColor: keyof ThemeColors, colors?: ThemeColors): string => {
    if (!textColor || !colors) return '';
    const parts = String(textColor).split('.');
    let current: ThemeColors | ColorShades | string = colors;
    for (const part of parts) {
        if (current && typeof current === 'object' && Object.hasOwn(current, part)) {
            current = current[part as keyof ThemeColors | keyof ColorShades];
        }
    }
    return typeof current === 'string' ? current : '';
};

const highlightText = (content: string, transform: TTextTransformation, colors?: ThemeColors) => {
    const textWithBreaks = content.replace(/\n/g, '<br />').trim();
    const highlight = transform.text;

    if (!highlight) {
        return textWithBreaks;
    }

    const map: number[] = [];
    let cleanText = '';
    let i = 0;
    while (i < textWithBreaks.length) {
        if (textWithBreaks[i] === '<') {
            const tagEnd = textWithBreaks.indexOf('>', i);
            if (tagEnd === -1) {
                cleanText += textWithBreaks[i];
                map.push(i);
                i++;
            } else {
                if (cleanText.length === 0 || cleanText[cleanText.length - 1] !== ' ') {
                    cleanText += ' ';
                    map.push(i);
                }
                i = tagEnd + 1;
            }
        } else {
            cleanText += textWithBreaks[i];
            map.push(i);
            i++;
        }
    }
    map.push(textWithBreaks.length);

    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const searchRegex = new RegExp(escapedHighlight.replace(/\s+/g, '\\s+'), 'gi');
    let matches = [...cleanText.matchAll(searchRegex)];

    if (matches.length === 0) {
        return textWithBreaks;
    } else if (!transform.isRepeatable) {
        matches = [matches[0]];
    }

    let resultString = textWithBreaks;
    for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i];
        if (match.index === undefined) continue;

        const matchEnd = match.index + match[0].length;
        const originalStart = map[match.index];
        const originalEnd = map[matchEnd];

        if (originalStart !== undefined && originalEnd !== undefined) {
            const styleParts: string[] = [];
            if (transform.textColor) {
                const color = getColorFromPalette(transform.textColor, colors);
                if (color) {
                    styleParts.push(`color: ${color}`);
                }
            }

            if (styleParts.length > 0 || transform.smallCaps || transform.useLocaleNumberFormat || transform.link || transform.actionName) {
                const originalSlice = resultString.slice(originalStart, originalEnd);
                const textToRender = transform.useLocaleNumberFormat ? (numberFormat(originalSlice, transform.precision) as string) + ' ' : originalSlice;

                const style = styleParts.join('; ');
                const finalClassName = clsx(transform.smallCaps && styles.smallCaps, transform.actionName && styles.actionable);
                const actionNameAttr = transform.actionName ? `data-action-name="${transform.actionName}"` : '';

                if (transform.link) {
                    resultString =
                        resultString.slice(0, originalStart) +
                        `<a href="${transform.link}" target="_blank" rel="noopener noreferrer" class="${finalClassName}" style="${style}">${textToRender}</a>` +
                        resultString.slice(originalEnd);
                } else {
                    const haveTransformation = style || finalClassName;
                    resultString = haveTransformation
                        ? resultString.slice(0, originalStart) +
                          `<span class="${finalClassName}" style="${style}" ${actionNameAttr}>${textToRender}</span>` +
                          resultString.slice(originalEnd)
                        : resultString.slice(0, originalStart) + textToRender + resultString.slice(originalEnd);
                }
            }
        }
    }

    return resultString;
};

type HighlighterProps = {
    content: string;
    colors?: ThemeColors;
    onClickAction?: (actionName: string) => void;
} & Pick<TCoreTextData, 'textTransformation'>;

export const Highlighter: React.FC<HighlighterProps> = ({ content, textTransformation, colors, onClickAction }) => {
    'use no memo';
    return useMemo(() => {
        // Server text is not type-checked at runtime: a number/boolean label must render, not throw on `.replace`.
        const safeContent = typeof content === 'string' ? content : content == null ? '' : String(content);
        let transformedText = highlightText(safeContent, {}, colors);

        if (Array.isArray(textTransformation) && textTransformation.length > 0) {
            textTransformation
                .filter((t) => t?.text && (t?.textColor || t?.smallCaps || t?.useLocaleNumberFormat || t?.link || t?.actionName))
                .forEach((transform) => {
                    transformedText = highlightText(transformedText, transform, colors);
                });
        }

        const options: HTMLReactParserOptions = {
            trim: false,
            replace: (domNode) => {
                //@ts-expect-error
                if (domNode?.attribs) {
                    //@ts-expect-error
                    const props = attributesToProps(domNode.attribs);
                    //@ts-expect-error
                    const children = domToReact(domNode.children, options);

                    //@ts-expect-error
                    if (domNode.name === 'a') {
                        return <a {...props}>{children}</a>;
                    }
                    //@ts-expect-error
                    if (domNode.name === 'span') {
                        if (props[DATA_ACTION_NAME] && onClickAction) {
                            return (
                                <span
                                    {...props}
                                    onClick={() => {
                                        //@ts-expect-error
                                        onClickAction(props[DATA_ACTION_NAME]);
                                    }}
                                >
                                    {children}
                                </span>
                            );
                        }
                        return <span {...props}>{children}</span>;
                    }
                }
            },
        };

        const sanitizedText = DOMPurify.sanitize(transformedText, {
            ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', DATA_ACTION_NAME],
            ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tg|svg|tel|callto|sms):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
        });

        return parse(sanitizedText, options);
    }, [content, textTransformation, colors, onClickAction]);
};
